import { fetchViewConfigFromCMS } from '@/graphql/utilties/fetchViewConfigFromCMS'
import { createResourceCollection } from '@/request'
import {
  ID,
  RequestTypes,
  ResourceMiddlewareFunction,
  ResourceModelQueryResult,
} from '@/request/types'
import { RootState } from '@/store'
import { Thermostat, Vehicle, VehicleCharger } from '@/types'
import { UtilityProgramDeviceTypeLowercased } from '@/types/device'
import { EnergySystem } from '@/types/energySystem'
import { Model } from '@/types/model'
import { behavioralIneligibilityReasonContent } from '@/types/programs-new/behavioral-requirements'
import { chargerIneligibilityReasonContent } from '@/types/programs-new/charger-requirements'
import {
  APIProgramRewardsMapping,
  APIProgramRewardsType,
  DeviceLimits,
  ProgramRewardsDeviceAmount,
  ProgramRewardsType,
  ProgramsNew,
} from '@/types/programs-new/programs-new'
import { thermostatIneligibilityReasonContent } from '@/types/programs-new/thermostat-requirements'
import { vehicleIneligibilityReasonContent } from '@/types/programs-new/vehicle-requirements'
import { SurveyResponse } from '@/types/surveyResponse'
import {
  UtilityProgram,
  UtilityProgramViewConfig,
} from '@/types/utilityProgram'
import getProgramEnrollmentExpiration from '@/utils/utility-program-helpers/getProgramEnrollmentExpiration'
import {
  getRewardAmounts,
  buildRewardsByCategory,
} from '@/utils/program-rewards/rewardCategoryHelpers'
import { createSelector } from '@reduxjs/toolkit'
import { match, P } from 'ts-pattern'

export type ProgramsNewModel = Model &
  ProgramsNew & {
    id: ID
    view_config_json?: UtilityProgramViewConfig
  }

async function populateViewConfigFromCMSMiddleware<T extends ProgramsNewModel>(
  requestData: { requestType: RequestTypes },
  data: T | T[],
): ReturnType<ResourceMiddlewareFunction<T>> {
  const type = requestData.requestType
  const middlewareInput: T[] = Array.isArray(data) ? data : [data]

  if (
    middlewareInput &&
    (type === RequestTypes.Fetch ||
      type === RequestTypes.Create ||
      type === RequestTypes.Update)
  ) {
    const programsWithViewConfigsPopulated = await Promise.all(
      middlewareInput.map((program) =>
        populateProgramAndDeviceEnrollmentViewConfigFromCMS(program),
      ),
    )

    return programsWithViewConfigsPopulated
  }

  return data
}

export async function populateProgramViewConfigFromCMS<
  T extends ProgramsNewModel,
>(program: T) {
  const cmsViewConfig = await fetchViewConfigFromCMS({
    utilityProgramId: program.utility_program.id,
  })

  return {
    ...program,
    utility_program: {
      ...program.utility_program,
      view_config_json: cmsViewConfig,
    },
    view_config_json: cmsViewConfig,
  }
}

async function populateDeviceEnrollmentViewConfig<
  T extends { group?: { alias?: string }; utility_program_id: string },
>(
  enrollment: T,
  utilityProgramId: number,
): Promise<T & { view_config_json?: any }> {
  const cmsViewConfig = await fetchViewConfigFromCMS({
    utilityProgramId,
    groupName: enrollment.group?.alias,
  })

  return {
    ...enrollment,
    view_config_json: cmsViewConfig,
  }
}

export async function populateProgramAndDeviceEnrollmentViewConfigFromCMS<
  T extends ProgramsNewModel,
>(program: T): Promise<T> {
  // First populate the program view config
  const programWithViewConfig = await populateProgramViewConfigFromCMS(program)

  // Then populate view configs for all device enrollments if they exist
  if (!programWithViewConfig.enrollment_overview) {
    return programWithViewConfig
  }

  const enrollmentOverview = programWithViewConfig.enrollment_overview
  const utilityProgramId = program.utility_program.id

  // Populate view configs for vehicle enrollments
  const vehicleEnrollments = enrollmentOverview.vehicle_enrollments
    ? await Promise.all(
        enrollmentOverview.vehicle_enrollments.map((enrollment) =>
          populateDeviceEnrollmentViewConfig(enrollment, utilityProgramId),
        ),
      )
    : []

  // Populate view configs for thermostat enrollments
  const thermostatEnrollments = enrollmentOverview.thermostat_enrollments
    ? await Promise.all(
        enrollmentOverview.thermostat_enrollments.map((enrollment) =>
          populateDeviceEnrollmentViewConfig(enrollment, utilityProgramId),
        ),
      )
    : []

  // Populate view configs for charger enrollments
  const chargerEnrollments = enrollmentOverview.charger_enrollments
    ? await Promise.all(
        enrollmentOverview.charger_enrollments.map((enrollment) =>
          populateDeviceEnrollmentViewConfig(enrollment, utilityProgramId),
        ),
      )
    : []

  // Populate view configs for behavioral enrollments
  const behavioralEnrollments = enrollmentOverview.behavioral_enrollments
    ? await Promise.all(
        enrollmentOverview.behavioral_enrollments.map((enrollment) =>
          populateDeviceEnrollmentViewConfig(enrollment, utilityProgramId),
        ),
      )
    : []

  return {
    ...programWithViewConfig,
    enrollment_overview: {
      ...enrollmentOverview,
      vehicle_enrollments: vehicleEnrollments,
      thermostat_enrollments: thermostatEnrollments,
      charger_enrollments: chargerEnrollments,
      behavioral_enrollments: behavioralEnrollments,
    },
  }
}

export const programsNewCollection = createResourceCollection<ProgramsNewModel>(
  {
    name: 'programsNewCollection',
    apiConfig: {
      path: 'programs/new',
      middlewares: [populateViewConfigFromCMSMiddleware],
    },
    selector: (state) => state.programsNew,
    computedId: (program) => program.utility_program?.id,
  },
)

export const programsNewCollectionReducer = programsNewCollection.slice.reducer

export const selectAvailablePrograms = (multiProgramEnabledOnly?: boolean) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return [] as ReturnType<typeof mapProgramToUtilityProgram>[]
      }
      return filterAvailablePrograms(programs, multiProgramEnabledOnly).map(
        (p) => mapProgramToUtilityProgram(p),
      ) as ReturnType<typeof mapProgramToUtilityProgram>[]
    },
  )

export function filterAvailablePrograms(
  p: ResourceModelQueryResult<ProgramsNewModel>[],
  multiProgramEnabledOnly?: boolean,
) {
  return (
    p
      .filter((p) =>
        match(p)
          .with(
            {
              enrollment_overview: P.nullish,
            },
            () => true,
          )
          .with(
            {
              enrollment_overview: {
                // TODO: change this to enrolled boolean later
                program_enrollment: { unenrolled_at: P.not(P.nullish) },
              },
            },
            () => true,
          )
          .otherwise(() => false),
      )
      // Is the program expired?
      .filter((p) => {
        const { isExpired } = getProgramEnrollmentExpiration(
          p.utility_program.preferred_display_timezone,
          p.view_config_json?.programCard?.enrollmentEndDate,
        )

        if (isExpired) {
          return p.view_config_json?.programCard?.rollingDeadline
        }

        return !isExpired
      })
      // Is the program multiprogram & is that what we're asking for?
      .filter((p) =>
        multiProgramEnabledOnly
          ? p.view_config_json?.useMultiProgramFlow
          : true,
      )
  )
}

export const selectEnrolledPrograms = (multiProgramEnabledOnly?: boolean) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return [] as ReturnType<typeof mapProgramToUtilityProgram>[]
      }
      return programs
        .filter((p) =>
          match(p)
            .with(
              {
                enrollment_overview: {
                  // TODO: change this to enrolled boolean later
                  program_enrollment: { unenrolled_at: P.nullish },
                },
              },
              () => true,
            )
            .otherwise(() => false),
        )
        .filter((p) =>
          multiProgramEnabledOnly
            ? p.view_config_json?.useMultiProgramFlow
            : true,
        )
        .map((p) => mapProgramToUtilityProgram(p))
        .toSorted((a, b) => a.id - b.id) as ReturnType<
        typeof mapProgramToUtilityProgram
      >[]
    },
  )

export const selectProgram = (utilityProgramId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return null
      }
      return programs.find((p) => p.utility_program.id === utilityProgramId)
    },
  )

export const selectAvailableProgram = (utilityProgramId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return null
      }
      return programs
        .filter(
          (p) =>
            !p.enrollment_overview ||
            p.enrollment_overview?.program_enrollment?.unenrolled_at,
        )
        .find((p) => p.utility_program.id === utilityProgramId)
    },
  )

export const selectEnrolledProgram = (utilityProgramId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return null
      }
      return programs
        .filter(
          (p) =>
            p.enrollment_overview &&
            !p.enrollment_overview?.program_enrollment?.unenrolled_at,
        )
        .find((p) => p.utility_program.id === utilityProgramId)
    },
  )

export const selectExistingEnrollmentProgram = (utilityProgramId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return null
      }
      return (
        programs.filter(
          (p) =>
            p.enrollment_overview && p.utility_program.id === utilityProgramId,
        )[0] ?? null
      )
    },
  )

const mapProgramToUtilityProgram = (
  p: ResourceModelQueryResult<ProgramsNewModel>,
) =>
  ({
    ...p.utility_program,
    total_incentives_usd: 0,
    referral_points: 0,
    verification_status:
      p.enrollment_overview?.program_enrollment?.verification_status,
    view_config_json: p.view_config_json as UtilityProgramViewConfig,
    max_device_count: p.utility_program.max_device_count,
    required_enrollment_survey_responses:
      p.utility_program.required_enrollment_survey_responses,
    enrollment_overview: p.enrollment_overview,
    device_eligibility: {
      eligible_charger_ids: [],
      eligible_vehicle_ids: [],
      eligible_thermostat_ids: [],
      ineligible_charger_ids: [],
      ineligible_thermostat_ids: [],
      ineligible_vehicle_ids: [],
      eligible_energy_system_ids: [],
      ineligible_energy_system_ids: [],
    },
    device_eligibility_overview: p.device_eligibility_overview,
    rewards: p.rewards,
  } as MappedUtilityProgram)

export type MappedUtilityProgram = UtilityProgram & {
  enrollment_overview: ProgramsNewModel['enrollment_overview']
  device_eligibility_overview: ProgramsNewModel['device_eligibility_overview']
  rewards: ProgramsNewModel['rewards']
}

export type ProgramDevices = {
  vehicles: Vehicle[]
  chargers: VehicleCharger[]
  thermostats: Thermostat[]
  energySystems: EnergySystem[]
}
export const selectProgramDevices = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      const devices = programs.find((p) => p.utility_program.id === programId)
        ?.device_eligibility_overview?.devices

      const programDevices: ProgramDevices = {
        vehicles: (devices?.vehicles ?? [])
          .map((device) => device.vehicle)
          .filter((vehicle) => vehicle !== null),
        chargers: (devices?.chargers ?? [])
          .map((device) => device.charger)
          .filter((charger) => charger !== null),
        thermostats: (devices?.thermostats ?? [])
          .map((device) => device.thermostat)
          .filter((thermostat) => thermostat !== null),
        energySystems: (devices?.energy_systems ?? [])
          .map((device) => device.energy_system)
          .filter((energySystem) => energySystem !== null),
      }
      return programDevices
    },
  )

export const selectProgramDeviceLimits = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      return (
        programs.find((p) => p.utility_program.id === programId)
          ?.device_limits ??
        ({
          vehicle: { min: null, max: null },
          thermostat: { min: null, max: null },
          charger: { min: null, max: null },
          behavioral: null,
          energy_system: { min: null, max: null },
          total_devices: { min: null, max: null },
        } satisfies DeviceLimits)
      )
    },
  )

export const selectProgramRewardDeviceLimits = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      return (
        programs.find((p) => p.utility_program.id === programId)
          ?.utility_program.reward_device_limits ??
        ({
          vehicle: { min: null, max: null },
          thermostat: { min: null, max: null },
          charger: { min: null, max: null },
          behavioral: { min: null, max: null },
          energy_system: { min: null, max: null },
          total_devices: { min: null, max: null },
        } satisfies DeviceLimits)
      )
    },
  )

export const selectProgramEnrolledDevices = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      const devices = programs.find(
        (p) => p.utility_program.id === programId,
      )?.enrollment_overview
      const programDevices = {
        vehicles: (devices?.vehicle_enrollments ?? []).map(
          (device) => device.vehicle,
        ),
        chargers: (devices?.charger_enrollments ?? []).map(
          (device) => device.charger,
        ),
        thermostats: (devices?.thermostat_enrollments ?? []).map(
          (device) => device.thermostat,
        ),
        energySystems: (devices?.energy_system_enrollments ?? []).map(
          (device) => device.energy_system,
        ),
      }
      return programDevices
    },
  )

export const selectAllProgramEnrolledDevices = () =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      return programs.map(
        ({ enrollment_overview: devices, utility_program }) => ({
          utilityProgramId: utility_program.id,
          devices: {
            vehicles: (devices?.vehicle_enrollments ?? []).map(
              (device) => device.vehicle,
            ),
            chargers: (devices?.charger_enrollments ?? []).map(
              (device) => device.charger,
            ),
            thermostats: (devices?.thermostat_enrollments ?? []).map(
              (device) => device.thermostat,
            ),
            behavioral: (devices?.behavioral_enrollments ?? []).map(
              (behavior) => behavior,
            ),
            energySystems: (devices?.energy_system_enrollments ?? []).map(
              (device) => device.energy_system,
            ),
          } as ProgramDevices,
        }),
      )
    },
  )

export const selectDeviceEligibilityMessages = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return null
      }
      const devices = programs.find((p) => p.utility_program.id === programId)
        ?.device_eligibility_overview?.devices
      if (!devices) return null
      return {
        vehicles: (devices.vehicles ?? [])
          .filter((d) => d.vehicle !== null)
          .filter((d) => d.is_eligible)
          .map((device) => ({
            id: device.id,
            vehicle: device.vehicle,
          })),
        chargers: (devices.chargers ?? [])
          .filter((d) => d.charger !== null)
          .filter((d) => d.is_eligible)
          .map((device) => ({
            id: device.id,
            charger: device.charger,
          })),
        thermostats: (devices.thermostats ?? [])
          .filter((d) => d.thermostat !== null)
          .filter((d) => d.is_eligible)
          .map((device) => ({
            id: device.id,
            thermostat: device.thermostat,
          })),
        behavioral: (devices.behavioral ?? [])
          .filter((b) => b.is_eligible && b.id)
          .map((behavior) => ({
            id: behavior.id as number,
          })),
      }
    },
  )

export type DeviceEligibility = ReturnType<
  ReturnType<typeof selectDeviceEligibilityMessages>
>

export const selectDeviceIneligibilityMessages = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return null
      }
      const devices = programs.find((p) => p.utility_program.id === programId)
        ?.device_eligibility_overview?.devices
      if (!devices) return null
      return getDeviceIneligiblityMessages(devices)
    },
  )

export const selectAllDeviceIneligibilityMessages = () =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return []
      }
      const deviceMessages = programs.map((p) => ({
        utilityProgramId: p.utility_program.id,
        messages: getDeviceIneligiblityMessages(
          p?.device_eligibility_overview?.devices,
        ),
      }))
      return deviceMessages
    },
  )

export type DeviceIneligibilityMessages = ReturnType<
  typeof getDeviceIneligiblityMessages
>
export const getDeviceIneligiblityMessages = ({
  chargers,
  thermostats,
  vehicles,
  behavioral,
}: ProgramsNewModel['device_eligibility_overview']['devices']) => {
  return {
    vehicles: (vehicles ?? [])
      .filter((d) => d.vehicle !== null)
      .filter((d) => !d.is_eligible)
      .map((device) => ({
        id: device.id,
        vehicle: device.vehicle,
        requirements: device.requirements_result
          .filter((r) => !r.is_eligible)
          .map((req) => ({
            key: req.key,
            content: vehicleIneligibilityReasonContent(req.key),
            isEligible: req.is_eligible,
          })),
      })),
    chargers: (chargers ?? [])
      .filter((d) => d.charger !== null)
      .filter((d) => !d.is_eligible && d.id)
      .map((device) => ({
        id: device.id,
        charger: device.charger,
        requirements: device.requirements_result
          .filter((r) => !r.is_eligible)
          .map((req) => ({
            key: req.key,
            content: chargerIneligibilityReasonContent(req.key),
            isEligible: req.is_eligible,
          })),
      })),
    thermostats: (thermostats ?? [])
      .filter((d) => d.thermostat !== null)
      .filter((d) => !d.is_eligible && d.id)
      .map((device) => ({
        id: device.id,
        thermostat: device.thermostat,
        requirements: device.requirements_result
          .filter((r) => !r.is_eligible)
          .map((req) => ({
            key: req.key,
            content: thermostatIneligibilityReasonContent(req.key),
            isEligible: req.is_eligible,
          })),
      })),
    behavioral: (behavioral ?? [])
      .filter((b) => !b.is_eligible && b.id)
      .map((behavior) => ({
        id: behavior.id as number,
        requirements: behavior.requirements_result.map((req) => ({
          key: req.key,
          content: behavioralIneligibilityReasonContent(req.key),
          isEligible: req.is_eligible,
        })),
      })),
  }
}

export type DeviceIneligibility = ReturnType<
  ReturnType<typeof selectDeviceIneligibilityMessages>
>

export const selectRequiredEnrollmentSurveyResponses = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      if (programs.length === 0) {
        return [] as SurveyResponse[]
      }
      const surveys =
        programs.find((p) => p.utility_program.id === programId)
          ?.utility_program.required_enrollment_survey_responses ?? []
      return surveys
    },
  )

export const isDeviceTypeAllowedInProgram = (
  programsNewModel: ProgramsNewModel,
  deviceType: 'vehicle' | 'thermostat' | 'charger' | 'energy_system',
): boolean => {
  const deviceLimits = programsNewModel.device_limits
  if (!deviceLimits[deviceType]) {
    return false
  }
  const enrollmentOverview = programsNewModel.enrollment_overview
  const currentEnrollments =
    enrollmentOverview?.[`${deviceType}_enrollments`]?.length ?? 0
  if (
    deviceLimits[deviceType].max === null ||
    deviceLimits[deviceType].max > currentEnrollments
  ) {
    return true
  }
  return false
}

export const hasExternalRewards = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      const program = programs.find((p) => p.utility_program.id === programId)
      if (!program || !program.rewards || program.rewards?.length === 0) {
        return false
      }

      return program.rewards.some((reward) => reward.external_payout)
    },
  )

export const selectProgramRewardsByCategory = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      const program = programs.find((p) => p.utility_program.id === programId)
      if (!program || !program.rewards || program.rewards?.length === 0) {
        return null
      }

      return buildRewardsByCategory(program.rewards)
    },
  )

type ProgramRewardByDeviceType = {
  type: ProgramRewardsType
} & Omit<ProgramRewardsDeviceAmount, 'deviceType'>
export const selectProgramRewardsByDeviceType = (programId: number) =>
  createSelector(
    (state: RootState) => programsNewCollection.selectors.selectAll(state),
    (programs) => {
      const program = programs.find((p) => p.utility_program.id === programId)
      if (!program || !program.rewards || program.rewards?.length === 0) {
        return null
      }

      return program.rewards
        .flatMap((reward) =>
          reward.eligible_device_types.map((deviceType) => ({
            deviceType:
              deviceType.toLowerCase() as UtilityProgramDeviceTypeLowercased,
            ...getRewardAmounts(reward),
            type: match({
              type: reward.type as APIProgramRewardsType,
              earnBeforeVerification: reward.earn_before_verification,
            })
              .with(
                { type: 'sign_up', earnBeforeVerification: true },
                () => 'application',
              )
              .with(
                { type: 'sign_up', earnBeforeVerification: false },
                () => 'enrollment',
              )
              .otherwise(({ type }) => APIProgramRewardsMapping[type]),
          })),
        )
        .reduce(
          (acc, { deviceType, dollarAmount, dollarAmountYearly, type }) => ({
            ...acc,
            [deviceType]: [
              ...acc[deviceType],
              {
                dollarAmount,
                dollarAmountYearly,
                type,
              },
            ],
          }),
          {
            vehicle: [] as ProgramRewardByDeviceType[],
            thermostat: [] as ProgramRewardByDeviceType[],
            home: [] as ProgramRewardByDeviceType[],
            charger: [] as ProgramRewardByDeviceType[],
            behavioral: [] as ProgramRewardByDeviceType[],
          },
        )
    },
  )

export type ProgramRewardsByDeviceType = ReturnType<
  ReturnType<typeof selectProgramRewardsByDeviceType>
>
